// Formatting helpers: SOL amounts, addresses, times. All amounts from the API are lamport strings (1 SOL = 1e9).
const LAMPORTS = 1000000000n;
/** Lamports → exact decimal SOL string ("0.000123456"). */
function formatLamports(v) {
  const neg = v < 0n, a = neg ? -v : v;
  const frac = (a % LAMPORTS).toString().padStart(9, "0").replace(/0+$/, "");
  return `${neg ? "-" : ""}${a / LAMPORTS}${frac ? `.${frac}` : ""}`;
}

export const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export function toBig(v) {
  try {
    if (typeof v === "bigint") return v;
    if (v === null || v === undefined || v === "") return 0n;
    if (typeof v === "number") return Number.isFinite(v) ? BigInt(Math.trunc(v)) : 0n;
    return BigInt(String(v).trim());
  } catch {
    return 0n;
  }
}

const trimZeros = (s) => (s.includes(".") ? s.replace(/\.?0+$/, "") : s);
const cut = (s, dp) => {
  const [i, f = ""] = s.split(".");
  return f && dp > 0 ? `${i}.${f.slice(0, dp)}` : i;
};

/**
 * Lamports → SOL string with sensible precision (truncated, never rounded up):
 * 0 → "0"; tiny → "<0.0001"; below 1 → 4 significant digits; below 1000 → 4 decimals; else 2 decimals with separators.
 */
export function formatSol(lamports, { min = 0.0001 } = {}) {
  const w = toBig(lamports);
  if (w === 0n) return "0";
  const neg = w < 0n;
  const s = formatLamports(neg ? -w : w);
  const n = Number(s);
  let out;
  if (n < min) out = `<${min}`;
  else if (n < 1) {
    const lead = (s.split(".")[1] || "").match(/^0*/)[0].length;
    out = trimZeros(cut(s, Math.min(9, lead + 4)));
  } else if (n < 1000) out = trimZeros(cut(s, 4));
  else out = Number(cut(s, 2)).toLocaleString("en-US", { maximumFractionDigits: 2 });
  return (neg ? "-" : "") + out;
}

/** An amount already in SOL (number or decimal string, e.g. priceSol) → display string, or null if missing. */
export function formatSolValue(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n === 0) return "0";
  const a = Math.abs(n);
  if (a >= 1000) return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  if (a >= 1) return trimZeros(n.toFixed(4));
  if (a >= 0.0001) return trimZeros(n.toFixed(Math.max(4, -Math.floor(Math.log10(a)) + 3)));
  // Tiny prices use the usual DEX shorthand: 0.0000000123 → "0.0₇123" (7 zeros after the point).
  let zeros = -Math.floor(Math.log10(a)) - 1;
  let scaled = Math.round(a * 10 ** (zeros + 3));
  if (scaled >= 1000) { zeros -= 1; scaled = Math.round(scaled / 10); } // e.g. 0.0000999999 rounds up to 0.0₃1
  if (zeros < 4) return trimZeros(n.toFixed(6));
  const digits = String(scaled).replace(/0+$/, "") || "0";
  const sub = String(zeros).replace(/\d/g, (d) => "₀₁₂₃₄₅₆₇₈₉"[d]);
  return `${n < 0 ? "-" : ""}0.0${sub}${digits}`;
}

export const shortAddr = (a, head = 4, tail = 4) => (a && a.length > head + tail + 2 ? `${a.slice(0, head)}…${a.slice(-tail)}` : a || "");

/** Basis points → "30%" (keeps one decimal when needed, e.g. 1250 → "12.5%"). */
export const pct = (bps) => `${trimZeros((Number(bps) / 100).toFixed(2))}%`;

/** Unix seconds, unix ms, numeric strings or ISO dates → ms since epoch (or null). */
export function toMs(t) {
  if (t === null || t === undefined || t === "") return null;
  if (typeof t === "number" || /^\d+(\.\d+)?$/.test(String(t))) {
    const n = Number(t);
    return n < 1e12 ? n * 1000 : n;
  }
  const d = Date.parse(t);
  return Number.isNaN(d) ? null : d;
}

export function timeAgo(t, now = Date.now()) {
  const ms = toMs(t);
  if (ms === null) return "";
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Age without "ago", for cards: "4m", "3h", "2d". */
export function age(t, now = Date.now()) {
  const ms = toMs(t);
  if (ms === null) return "";
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return "new";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/** Milliseconds left → "2h 14m", "14m 05s", "45s". */
export function countdown(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

export function formatDate(t) {
  const ms = toMs(t);
  if (ms === null) return "";
  return new Date(ms).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export const plural = (n, one, many = `${one}s`) => `${Number(n).toLocaleString("en-US")} ${Number(n) === 1 ? one : many}`;

export const handle = (h) => (h ? `@${String(h).replace(/^@/, "")}` : "");
